import React, { useCallback, useEffect, useRef, useState } from 'react';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import { formatUrl } from '@plone/volto/helpers/Api/Api';

import PollForm from './PollForm';
import PollResults from './PollResults';
import type { PollBlockData } from './schema';
import type { PollFetchStatus, PollState } from './types';

interface ViewProps {
  data: PollBlockData;
  block: string;
  properties?: { '@id'?: string };
  pathname?: string;
  mode?: 'view' | 'edit';
}

function buildEditorOptions(data: PollBlockData) {
  return (data.options ?? [])
    .map((option, index) => ({
      id: `option_${index}`,
      label: (option?.label ?? '').trim(),
      votes: 0,
      percentage: 0,
    }))
    .filter((option) => option.label.length > 0);
}

function pollEndpoint(contextPath: string, blockUid: string) {
  const path = flattenToAppURL(contextPath);
  const cleaned = (path || '').replace(/\/$/, '');
  return formatUrl(`${cleaned}/@poll/${blockUid}`);
}

const View: React.FC<ViewProps> = ({
  data,
  block,
  properties,
  pathname,
  mode,
}) => {
  const resultsRef = useRef<HTMLDivElement>(null);
  const contextUrl = properties?.['@id'] || pathname || '';

  const [status, setStatus] = useState<PollFetchStatus>('idle');
  const [pollState, setPollState] = useState<PollState | null>(null);
  const [voteError, setVoteError] = useState<string | null>(null);
  const [justVoted, setJustVoted] = useState(false);
  const [votedOptionId, setVotedOptionId] = useState<string | null>(null);

  const isEditMode = mode === 'edit';
  const question = (data.question ?? '').trim();
  const description = (data.description ?? '').trim() || undefined;
  const editorOptions = buildEditorOptions(data);
  const hasMinimumConfig = question.length > 0 && editorOptions.length >= 2;

  useEffect(() => {
    if (isEditMode || !hasMinimumConfig || !contextUrl) {
      return;
    }
    let aborted = false;
    setStatus('loading');
    const url = pollEndpoint(contextUrl, block);
    fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json() as Promise<PollState>;
      })
      .then((payload) => {
        if (aborted) return;
        setPollState(payload);
        setStatus(payload.user_voted ? 'voted' : 'ready');
      })
      .catch(() => {
        if (aborted) return;
        setStatus('error');
      });
    return () => {
      aborted = true;
    };
  }, [block, contextUrl, hasMinimumConfig, isEditMode]);

  const handleVote = useCallback(
    async (optionId: string) => {
      if (!contextUrl) return;
      setStatus('voting');
      setVoteError(null);
      try {
        const response = await fetch(pollEndpoint(contextUrl, block), {
          method: 'POST',
          credentials: 'include',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ option: optionId }),
        });
        if (response.status === 409) {
          setVoteError('Você já votou nesta enquete.');
          const refetched = await fetch(pollEndpoint(contextUrl, block), {
            credentials: 'include',
            headers: { Accept: 'application/json' },
          });
          if (refetched.ok) {
            const payload = (await refetched.json()) as PollState;
            setPollState(payload);
            setStatus('voted');
          } else {
            setStatus('error');
          }
          return;
        }
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const payload = (await response.json()) as PollState;
        setPollState(payload);
        setJustVoted(true);
        setVotedOptionId(optionId);
        setStatus('voted');
      } catch {
        setVoteError('Não foi possível registrar seu voto. Tente novamente.');
        setStatus('ready');
      }
    },
    [block, contextUrl],
  );

  useEffect(() => {
    if (status === 'voted' && resultsRef.current) {
      resultsRef.current.focus();
    }
  }, [status]);

  if (!hasMinimumConfig) {
    return isEditMode ? (
      <div className="poll-block poll-block--empty">
        Configure a pergunta e as opções no painel lateral.
      </div>
    ) : null;
  }

  // No modo de edição renderizamos um preview estático com os dados do editor,
  // sem disparar requisições durante a autoria.
  if (isEditMode) {
    return (
      <div className="poll-block poll-block--preview">
        <PollForm
          question={question}
          description={description}
          options={editorOptions}
          onSubmit={() => {
            /* preview only */
          }}
        />
      </div>
    );
  }

  if (status === 'loading' || status === 'idle') {
    return (
      <div
        className="poll-block poll-block--loading"
        role="status"
        aria-live="polite"
      >
        Carregando enquete…
      </div>
    );
  }

  if (status === 'error' || !pollState) {
    return (
      <div className="poll-block poll-block--error" role="alert">
        Não foi possível carregar a enquete.
      </div>
    );
  }

  if (status === 'voted') {
    return (
      <div className="poll-block poll-block--voted">
        <PollResults
          ref={resultsRef}
          question={pollState.question}
          options={pollState.options}
          total={pollState.total}
          justVoted={justVoted}
          votedOptionId={votedOptionId}
        />
      </div>
    );
  }

  return (
    <div className="poll-block poll-block--ready">
      <PollForm
        question={pollState.question}
        description={description}
        options={pollState.options}
        total={pollState.total}
        onSubmit={handleVote}
        submitting={status === 'voting'}
        errorMessage={voteError}
      />
    </div>
  );
};

export default View;
